/** Eigene Statistik-Kachel (BE-06 Teil 3b): Datenquelle wählen, Darstellung wechseln (Zahl, Säulen, Linie, Tabelle). */
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { statistikAendern } from '@/db/repositories/oberflaeche';
import { DARSTELLUNGEN, QUELLEN, reihe, statistikAus, type StatistikDarstellung, type StatistikQuelle } from '@/domain/oberflaeche/statistik';
import { LinienDiagramm } from '@/ui/composites/LinienDiagramm';
import { Saeulen } from '@/ui/composites/Saeulen';
import { Kennzahl, StatKarte } from '@/ui/composites/Statistik';
import { useBereichDaten } from '@/modules/areas/board/bereichDaten';
import { useHabitsWithStats } from '@/modules/habits/useHabits';
import { today } from '@/lib/datetime';

const feld = 'h-8 rounded-md border border-line bg-surface px-2 text-[12px] text-ink focus:border-accent focus:outline-none';

export function StatistikEigeneKachel({ id }: { id: string }) {
  const s = useLiveQuery(async () => statistikAus(await db.oberflaeche.get(id)), [id]);
  const { area, tasks } = useBereichDaten();
  const habits = useHabitsWithStats(false, area.id);
  if (!s) return null;
  const heute = today();
  const punkte = reihe(s.quelle, { tasks, habits }, heute);
  const q = QUELLEN[s.quelle];
  const max = Math.max(1, ...punkte.map((p) => p.wert));
  const fmt = (v: number) => (s.quelle === 'gewohnheiten' ? `${v} %` : String(v));
  const aktuell = punkte.at(-1)!.wert;
  const schnitt = Math.round((punkte.reduce((a, p) => a + p.wert, 0) / punkte.length) * 10) / 10;
  return (
    <StatKarte titel={s.name} frage={q.frage}>
      {s.darstellung === 'zahl' && <Kennzahl label="Diese Woche" wert={fmt(aktuell)} hinweis={`Ø ${s.quelle === 'gewohnheiten' ? `${schnitt} %` : schnitt} der letzten 8 Wochen`} />}
      {s.darstellung === 'saeulen' && <Saeulen label={`${q.label} (8 Wochen)`} einheit={q.einheit} links={punkte[0]!.kurz} rechts="diese Woche" punkte={punkte.map((p) => ({ key: p.key, titel: p.titel, wert: p.wert }))} format={fmt} />}
      {s.darstellung === 'linie' && (
        <LinienDiagramm titel={`${q.label} (8 Wochen)`} punkte={punkte.map(({ key, titel, kurz }) => ({ key, titel, kurz }))} einheit={s.quelle === 'gewohnheiten' ? '%' : ''}
          reihen={[{ id: 'w', name: q.label, platz: 0, werte: punkte.map((p) => p.wert) }]} min={0} max={s.quelle === 'gewohnheiten' ? 100 : max} ticks={s.quelle === 'gewohnheiten' ? [0, 50, 100] : [0, Math.round(max / 2), max]} />
      )}
      {s.darstellung === 'tabelle' && (
        <table className="w-full text-sm">
          <caption className="sr-only">{q.label}</caption>
          <thead><tr className="text-left text-[11px] text-ink-faint"><th scope="col" className="py-1 font-normal">Woche</th><th scope="col" className="py-1 text-right font-normal">{q.einheit}</th></tr></thead>
          <tbody className="divide-y divide-line">{[...punkte].reverse().map((p) => <tr key={p.key}><td className="py-1">{p.titel}</td><td className="py-1 text-right tabular-nums">{fmt(p.wert)}</td></tr>)}</tbody>
        </table>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
        <label className="flex items-center gap-1.5">Daten
          <select aria-label={`Datenquelle von ${s.name}`} value={s.quelle} className={feld} onChange={(e) => void statistikAendern(id, { quelle: e.target.value as StatistikQuelle })}>
            {(Object.keys(QUELLEN) as StatistikQuelle[]).map((k) => <option key={k} value={k}>{QUELLEN[k].label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1.5">Darstellung
          <select aria-label={`Darstellung von ${s.name}`} value={s.darstellung} className={feld} onChange={(e) => void statistikAendern(id, { darstellung: e.target.value as StatistikDarstellung })}>
            {(Object.keys(DARSTELLUNGEN) as StatistikDarstellung[]).map((k) => <option key={k} value={k}>{DARSTELLUNGEN[k]}</option>)}
          </select>
        </label>
      </div>
    </StatKarte>
  );
}
